<template>
  <!-- QA: Keep it? -->
  <CheckoutLayout>
    <DemoWarningModal v-if="brandconfig.BRAND === 'demoshop'" />

    <!-- INFO: Status Bar -->
    <div>
      <div class="pb-3">
        <div class="flex items-center justify-center text-base text-gray-600 mb-2">
          <span class="bg-primary text-primary-foreground rounded-full size-6 flex items-center justify-center">
            <IcBaselineCheck />
          </span>
          <div class="flex-1 h-px bg-primary mx-2"></div>
          <span
            class="bg-primary text-primary-foreground rounded-full size-6 flex font-semibold items-center justify-center"
            >2</span
          >
          <div class="flex-1 h-px bg-primary mx-2"></div>
          <span
            class="border-2 border-muted-foreground text-muted-foreground rounded-full size-6 flex items-center justify-center"
            >3</span
          >
        </div>
        <h2 class="text-center text-xs font-semibold text-foreground">{{ $t("General info and contact details") }}</h2>
      </div>
      <hr class="breakout" />
      <CartReservation class="text-foreground sm:mx-0 py-4" />
      <hr class="breakout" />
      <template v-if="cartStore.items.length">
        <TicketsOverview class="py-4" />
        <hr class="breakout" />
      </template>

      <!-- INFO: Form -->
      <form class="text-foreground pb-6" @submit.prevent="submit">
        <div class="grid gap-4 lg:gap-8">
          <div class="space-y-4 lg:space-y-8">
            <CustomerInvoicing v-if="isB2C" />
            <GuestData />
          </div>

          <div class="space-y-4 lg:space-y-8">
            <CheckoutCard class="gap-3 lg:gap-6 h-fit">
              <template v-if="true">
                <Button
                  type="submit"
                  color="success"
                  class="w-full items-center gap-2 h-12"
                  :disabled="!canProceedToNext"
                  data-testid="proceed-to-confirmation-button">
                  {{ $t("To payment") }}
                </Button>
              </template>

              <!-- QA:  Do we need it here or next-->
            </CheckoutCard>
          </div>
        </div>
      </form>
      <!-- Confirmation Modal -->
      <component :is="render" />
    </div>
  </CheckoutLayout>
</template>

<script setup lang="ts">
import CheckoutCard from "../CheckoutCard.vue";
import CustomerInvoicing from "../CustomerInvoicing.vue";
import GuestData from "../GuestData.vue";
import { onBeforeRouteLeave, useRouter } from "vue-router";
import { useCartStore } from "@/store/cart.ts";
import { computed, ref, watch } from "vue";
import { useUserStore } from "@/store/user.js";
import brandconfig from "@/brandconfig";
import { isB2C } from "@/brandconfig";
import { DemoWarningModal } from "@/components/home/index";
import { Button } from "@xs2event/js-lib/components";
import { useI18n } from "vue-i18n";
import CheckoutLayout from "./CheckoutLayout.vue";
import IcBaselineCheck from "~icons/ic/baseline-check";
import TicketsOverview from "@/components/checkout/b2c/ticketSummary/TicketsOverview.vue";
import CartReservation from "@/components/cart/CartReservation.vue";

import { object, string, boolean, phone, useForm, date, mixed, type InferType } from "@xs2event/js-lib/validation";
import { provideCheckoutContext } from "../checkoutContext";
import { until, watchOnce } from "@vueuse/core";
import { useConfirm } from "@xs2event/js-lib/composables";

const { guestConditionOverwrites, form: formReference } = provideCheckoutContext();

const { t } = useI18n();
const cartStore = useCartStore();
const userStore = useUserStore();

const canProceedToNext = computed(() => {
  return !!cartStore.timeLeft;
});

const guestSchemas = {
  first_name: string().required(),
  last_name: string().required(),
  street_name: string().required(),
  additional_street_name: string().required(),
  zip: string().required(),
  contact_email: string().email().required(t("string.email")),
  contact_phone: phone().required(),
  passport_number: string().required(),
  member_id: string().required(),
  supported_team: string().required(),
  country_of_residence: string().required(),
  city: string().required(),
  province: string().required(),
  date_of_birth: date().max(new Date()).required(),
  gender: string().required(),
} as const;

const contactDetailsSchema = object({
  first_name: string().required(),
  iso_country: string().required(),
  last_name: string().required(),
  marketing_optin: boolean(),
  phonenumber: phone().required(),
  preferred_contact_language: string(),
}).required();

const bookingDetailsSchema = computed(() =>
  object({
    isB2C: boolean().required(),
    email: userStore.isUser ? string().email() : string().email().required(t("string.email")),
    addGuestsLater: boolean(),
    contactDetails: isB2C ? contactDetailsSchema : mixed().notRequired(),
    guests: mixed().when("addGuestsLater", {
      is: true,
      then: () => mixed().notRequired(),
      otherwise: () =>
        object({
          ...Object.fromEntries(
            cartStore.conditions.reduce(
              (accumulator, current) => {
                if (!current.guests.length) return accumulator;

                const _guestSchemas = Object.fromEntries(
                  current.guests.map((guest, guestIndex) => {
                    const fields = Object.entries(
                      guestConditionOverwrites.value.get(`${current.ticket_id}[${guestIndex}]`) || guest
                    ).reduce(
                      (acc, [key]) => {
                        if (key !== "lead_guest") {
                          acc[key] = guestSchemas[key as keyof typeof guestSchemas];
                        }
                        return acc;
                      },
                      {} as Record<string, unknown>
                    );

                    return [`guest_${guestIndex}`, object(fields)];
                  })
                );

                accumulator.push([current.ticket_id, object(_guestSchemas)]);

                return accumulator;
              },
              [] as [string, unknown][]
            )
          ),
        }),
    }),
  })
);

type FormData = InferType<(typeof bookingDetailsSchema)["value"]>;
// Create initial values with potential pre-fill from stored data
const getInitialValues = () => ({
  isB2C,
  // Pre-fill from stored booking details if available
  ...(cartStore.bookingDetails || {}),
  ...{
    booking: {
      country: userStore?.distributor.iso_country,
    },
  },
});

const form = useForm<FormData>({
  validationSchema: bookingDetailsSchema,
  initialValues: getInitialValues(),
});

formReference.value = form;

// Watch for changes in stored booking details (when navigating back from step 2)
watch(
  () => cartStore.bookingDetails,
  (newBookingDetails, oldBookingDetails) => {
    if (newBookingDetails && newBookingDetails !== oldBookingDetails) {
      form.resetForm({
        values: {
          ...getInitialValues(),
          ...newBookingDetails,
        },
      });
    }
  },
  { immediate: true, deep: true }
);

watchOnce(
  () => cartStore.reservationId,
  (value) => {
    if (!value) return;
    cartStore.resetTimer();
  }
);

// QA: What is the business logic here?
watch(
  () => form.values.contactDetails,
  (contactDetails) => {
    if (!contactDetails) return;

    const hasAny = Object.values(contactDetails).some((v) => v && v !== "");
    if (!hasAny) return;

    cartStore.conditions.forEach((condition) => {
      const guestPath = `guests.${condition.ticket_id}.guest_0`;
      Object.keys(contactDetailsSchema.fields).forEach((key) => {
        // Map keys of contactDetails to guest keys.
        const guestKey = key === "iso_country" ? "country_of_residence" : key === "phonenumber" ? "contact_phone" : key;

        if (contactDetails[key] !== undefined && contactDetails[key] !== "") {
          form.setFieldValue(`${guestPath}.${guestKey}`, contactDetails[key]);
        }
      });
      if (form.values.email) {
        form.setFieldValue(`${guestPath}.contact_email`, form.values.email);
      }
    });
  },
  { deep: true }
);
// QA: What is the business logic here?
if (isB2C) {
  watch(
    () => form.values.email,
    (email) => {
      if (!email) return;
      cartStore.conditions.forEach((condition) => {
        const guestPath = `guests.${condition.ticket_id}.guest_0`;
        form.setFieldValue(`${guestPath}.contact_email`, email);
      });
    }
  );
}

const { promptConfirm, render } = useConfirm({
  title: t("Leave page"),
  content: t("Are you sure you want to leave this page? All changed made will be gone."),
  onConfirm: () => {
    confirmed.value = true;
  },
});

const confirmed = ref(false);
onBeforeRouteLeave(async (to, from) => {
  const isLeavingCheckout = from.path.includes("/checkout") && !to.path.includes("/checkout");

  if (form.meta.value.dirty && isLeavingCheckout) {
    promptConfirm();
    await until(confirmed).toBeTruthy();
    // Reset confirmed state for next time
    confirmed.value = false;
    return true; // Allow navigation to continue
  }
  return true; // Allow navigation if no confirmation needed
});

const router = useRouter();

const submit = form.handleSubmit(async (values) => {
  // Store booking details in cart store for next step
  cartStore.bookingDetails = values;
  router.push({ name: "checkout-order-confirmation" });
});
</script>
